import Loading from "../../components/core/blocks/Loading";
import { useDates } from "../../dates/DatesContext";
import { useFetch } from "../../hooks/api";
import { useState } from "react";
import { Row, Col, Nav, Badge } from "react-bootstrap";
import { Card } from "react-bootstrap/esm";
import { useTranslation } from "react-i18next";
import ProductImage from "../../components/custom/category/listings/ProductImage";
import { INVENTORY_STATES, SALE_STATES } from "../../components/core/blocks/AdPlan/constants";
import PriceHistorySection from "./cards/PriceHistory";
import ImpactSection from "./cards/Impact";
import { DemandChartsSection } from "./cards/DemandTrend";
import Overview from "./cards/Overview";
import CompetitorsSection from "./cards/Competitors";
import ConfirmationSection from "./cards/Confirmation";
import moment from "moment";
import { Link } from "@tanstack/react-router";
import BuyBoxOffers from "./cards/BuyBoxOffers";
import { useModuleEnabled } from "../../redux/selectors/modules";
import { z } from "zod";
import { isValidDataframeDate } from "../../utils/formatText";
import TrellisNavLink from "../../components/core/basic/NavLink";
import { useAppSelector } from "../../redux/hooks";
import { keepPreviousData } from "@tanstack/react-query";
import { PricingProductUnavailable } from "./components/PricingProductUnavailable";

interface Product {
  product_title: string;
  asin: string;
  sku: string;
  sales_state: string;
  stock_status: string;
}

// There's a _lot_ returned from this endpoint. Only typing out fields used in this component, expand as needed.
interface PricingProduct {
  confidence: string;
  enroll_date: string;
  price_override_end: string;
  plan_id: number;
}

interface Props {
  productId: string;
  additionalBadges?: { text: string; color?: string }[];
}

const PRICING_PRODUCT_TABS = new Set(["price_history", "ad_affect", "impact", "confirmation"]);

export const ProductCard = ({ productId, additionalBadges = [] }: Props) => {
  const { t } = useTranslation(["pricingv2", "advertising"]);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const { start, end, preStart, preEnd, group } = useDates();
  const hasBuyBoxModule = useModuleEnabled("BUYBOX");

  const filters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    group_by: "product_id",
  };
  const { isLoading, data: product } = useFetch<Product>(
    ["products", productId, group, start, end, preStart, preEnd],
    `/api/gvads/productid/${productId}`,
    {},
    { select: (res) => res.data, placeholderData: keepPreviousData },
  );

  const { data, isLoading: isOverviewDataLoading } = useFetch(
    ["product_report"],
    `/api/data_report/product_report?product_id=${productId}`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );
  const salesStateArray = [SALE_STATES.BEST_SELLER!, SALE_STATES.CLEARANCE!, SALE_STATES.NEW_PRODUCT!];
  const inventoryStateArray = [
    INVENTORY_STATES.OVERSTOCK!,
    INVENTORY_STATES.LOW_STOCK!,
    INVENTORY_STATES.OUT_OF_STOCK!,
  ];
  const salesLabelSelector = salesStateArray.find((s) => s.value === product?.sales_state)?.label;
  const inventoryBadgeSelector = inventoryStateArray.find((s) => s.value === product?.stock_status)?.label;
  const salesLabel = salesLabelSelector ? t(salesLabelSelector) : undefined;
  const inventoryBadgeText = inventoryBadgeSelector ? t(inventoryBadgeSelector) : undefined;
  const [tab, setTab] = useState("overview");

  const {
    data: pricingProduct,
    isLoading: isPricingProductDataLoading,
    error: pricingProductError,
  } = useFetch<PricingProduct>(
    ["product_pricing", productId],
    `/pricing/products/${productId}/`,
    { get_primary: "false" },
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const pricingProductUnavailable = !isPricingProductDataLoading && !pricingProduct;

  return (
    <>
      {isLoading || isPricingProductDataLoading || isOverviewDataLoading || !product ?
        <Loading fullPage={false} height={"300px"} />
      : <Card className="fs-standard mb-0">
          <Card.Body>
            <Row>
              <Col xs={12} xl={8}>
                <div style={{ display: "flex" }}>
                  <div style={{ width: "120px", paddingRight: "20px" }}>
                    <ProductImage id={productId} style={{ borderRadius: 15 }} width={100} height={100} />
                  </div>
                  <div>
                    <Row>
                      <Col xs={12}>
                        <h3
                          style={{
                            marginBottom: "0.2rem",
                            marginTop: "0",
                            fontSize: 22,
                          }}
                        >
                          {product.product_title}
                        </h3>
                      </Col>
                      <Col xs={12}>
                        {product.asin} - {product.sku}
                        &nbsp;-&nbsp;
                        <Badge
                          className={`badge bg-trellis-${pricingProduct?.enroll_date ? "green" : "lightgrey"} text-end`}
                          pill
                        >
                          {pricingProduct?.enroll_date && <span>{t(($) => $.pricingv2["Enrolled"])}</span>}
                          {!pricingProduct?.enroll_date && !pricingProduct?.price_override_end && (
                            <span>{t(($) => $.pricingv2["Not Enrolled"])}</span>
                          )}
                        </Badge>
                        {isValidDataframeDate(pricingProduct?.price_override_end) &&
                          moment().isBefore(pricingProduct.price_override_end) && (
                            <Badge className={`badge bg-trellis-purple text-end ms-3`} pill>
                              {t(($) => $.pricingv2["Fixed Price"])}
                            </Badge>
                          )}
                      </Col>
                      <Col xs={12}>
                        {inventoryBadgeText && (
                          <Badge
                            className="badge bg-trellis-purple"
                            style={{
                              marginRight: "0.5rem",
                              backgroundColor: "#c43066",
                            }}
                            pill
                          >
                            {inventoryBadgeText}
                          </Badge>
                        )}
                        {salesLabel && (
                          <Badge className="bg-trellis-fuchsia" pill>
                            {salesLabel}
                          </Badge>
                        )}
                        {additionalBadges.map((badge, index) => (
                          <Badge
                            className="badge bg-trellis-mauve"
                            style={{
                              marginLeft: "0.5rem",
                              backgroundColor: badge.color,
                            }}
                            pill
                            key={`${badge.text}-${index}`}
                          >
                            {badge.text}
                          </Badge>
                        ))}
                      </Col>
                    </Row>
                  </div>
                </div>
              </Col>
              <Col className="text-center" xs={12} xl={4}>
                {(pricingProduct?.confidence || pricingProduct?.enroll_date) && (
                  <Row>
                    <Col>
                      <h6 style={{ fontWeight: 600 }} className="fs-standard text-uppercase pt-1 pe-3">
                        {t(($) => $.pricingv2["Enroll Date"])}
                      </h6>
                      {z.string().datetime().safeParse(pricingProduct.enroll_date).success ?
                        <span>{moment(pricingProduct.enroll_date).format("MM/DD/YYYY")}</span>
                      : <span>{t(($) => $.pricingv2["n/a"])}</span>}
                    </Col>
                    <Col>
                      <span>&nbsp;</span>
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
            <Row>
              <Col className="mt-5 ph-4 mb-3 border-bottom" xs={12}>
                <Nav
                  variant="underline"
                  onSelect={(k) => {
                    setTab(k!);
                  }}
                  activeKey={tab}
                >
                  <TrellisNavLink eventKey="overview">{t(($) => $.pricingv2["Overview"])}</TrellisNavLink>
                  <TrellisNavLink eventKey="price_history">{t(($) => $.pricingv2["Price History"])}</TrellisNavLink>
                  <TrellisNavLink eventKey="ad_affect">
                    {t(($) => $.pricingv2["Advertising Effectiveness"])}
                  </TrellisNavLink>
                  <TrellisNavLink eventKey="competitors">{t(($) => $.pricingv2["Competitors"])}</TrellisNavLink>
                  <TrellisNavLink eventKey="impact" visible={pricingProduct?.enroll_date !== undefined}>
                    {t(($) => $.pricingv2["Impact"])}
                  </TrellisNavLink>

                  <TrellisNavLink eventKey="confirmation" visible={pricingProduct?.enroll_date !== undefined}>
                    {t(($) => $.pricingv2["Price Changes"])}
                  </TrellisNavLink>

                  <TrellisNavLink eventKey="buy_box" visible={hasBuyBoxModule}>
                    {t(($) => $.pricingv2["Buy Box Offers"])}
                  </TrellisNavLink>
                </Nav>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                {pricingProductUnavailable && PRICING_PRODUCT_TABS.has(tab) ?
                  <PricingProductUnavailable error={pricingProductError} />
                : <>
                    {tab === "overview" && (
                      <Overview
                        isPricingProductDataLoading={isPricingProductDataLoading}
                        data={data}
                        isLoading={isOverviewDataLoading}
                      />
                    )}
                    {tab === "price_history" && (
                      <>
                        <Card>
                          <DemandChartsSection
                            pricingProduct={pricingProduct}
                            product={product}
                            isPricingProductDataLoading={isPricingProductDataLoading}
                            pricingProductError={pricingProductError}
                          />
                        </Card>
                        <hr />
                        <PriceHistorySection
                          pricingProduct={pricingProduct}
                          pricingProductError={pricingProductError}
                          marketPlace={marketPlace}
                          isPricingProductDataLoading={isPricingProductDataLoading}
                          isLoading={false}
                        />
                      </>
                    )}
                    {tab === "ad_affect" && (
                      <PriceHistorySection
                        pricingProduct={pricingProduct}
                        pricingProductError={pricingProductError}
                        marketPlace={marketPlace}
                        isPricingProductDataLoading={isPricingProductDataLoading}
                        isLoading={isOverviewDataLoading}
                        advertising={true}
                      />
                    )}
                    {tab === "impact" && <ImpactSection pricingProduct={pricingProduct} marketPlace={marketPlace} />}
                    {tab === "confirmation" && (
                      <ConfirmationSection
                        pricingProduct={pricingProduct}
                        marketPlace={marketPlace}
                        pricingPlan={undefined}
                        date={undefined}
                      />
                    )}
                    {tab === "competitors" && <CompetitorsSection productId={productId} marketPlace={marketPlace} />}
                    {tab === "buy_box" && <BuyBoxOffers asin={product.asin} />}
                  </>
                }
              </Col>
            </Row>
            {pricingProduct?.plan_id && (
              <Row>
                <Col className="text-center py-2" xs={12}>
                  <Link to="/user/pricing/plan/$plan" params={{ plan: String(pricingProduct.plan_id) }}>
                    {t(($) => $.pricingv2["View Pricing Plan"])}
                  </Link>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>
      }
    </>
  );
};
